interface CreditBadgeProps {
  credits: number;
}

/**
 * CreditBadge — a refined chip used in the top header.
 *
 * Visual recipe:
 *   - Hairline stadium frame (no gold tint fill — that read like a dashboard pill).
 *   - The GoldLeafMark (a tiny sheet of gold leaf) anchored left, font-display
 *     numeral, then a small-caps "leaves" label that yields on narrow screens
 *     so the header instrument cluster always fits.
 *   - Same class set works in both .theme-light and .theme-dark.
 */
export function CreditBadge({ credits }: CreditBadgeProps) {
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full border border-hairline-strong/70 bg-surface-elevated/60 px-2.5 py-1"
      aria-label={`${credits} leaves remaining`}
      title={`${credits} leaves remaining`}
    >
      <GoldLeafMark className="h-3 w-3" />
      {/* Keyed by value so a spend/refill rises in rather than mutating in place. */}
      <span
        key={credits}
        className="inline-block font-display text-micro font-semibold leading-none text-ink motion-safe:animate-count-up"
      >
        {credits}
      </span>
      <span
        className="hidden font-display text-[0.625rem] uppercase tracking-[0.16em] text-ink-subtle sm:inline"
        style={{ fontFeatureSettings: "'smcp' on, 'c2sc' on" }}
      >
        leaves
      </span>
    </span>
  );
}

/**
 * GoldLeafMark — the credit unit's mark: a bevel-cut gold stone in the brand
 * ramp. Four gradient crown facets lit from the upper left, a bright table
 * facet, pale seams, a lit/shadowed rim, a soft inner glow, and a four-point
 * sparkle. Reads as set gold at 12px and shows its cut at dashboard scale.
 * (Gradient ids are deterministic; multiple instances per page share them,
 * which is safe because the defs are identical.)
 */
export function GoldLeafMark({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className={className ?? 'h-3.5 w-3.5'}>
      <defs>
        <linearGradient id="glmTL" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#fff3ce" />
          <stop offset="1" stopColor="#e3bd66" />
        </linearGradient>
        <linearGradient id="glmTR" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#eccf85" />
          <stop offset="1" stopColor="#a87f2c" />
        </linearGradient>
        <linearGradient id="glmBL" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#c19438" />
          <stop offset="1" stopColor="#8a6020" />
        </linearGradient>
        <linearGradient id="glmBR" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#96691f" />
          <stop offset="1" stopColor="#5e3e10" />
        </linearGradient>
        <linearGradient id="glmTab" x1="0" y1="0" x2="0.7" y2="1">
          <stop offset="0" stopColor="#fff7dd" />
          <stop offset="0.55" stopColor="#ecc878" />
          <stop offset="1" stopColor="#c2922f" />
        </linearGradient>
        <radialGradient id="glmGlow" cx="0.38" cy="0.34" r="0.75">
          <stop offset="0" stopColor="#fff3ce" stopOpacity="0.55" />
          <stop offset="0.55" stopColor="#fff3ce" stopOpacity="0" />
        </radialGradient>
      </defs>
      {/* crown facets */}
      <path d="M12 1.6 L2 12 L12 12 Z" fill="url(#glmTL)" />
      <path d="M12 1.6 L22 12 L12 12 Z" fill="url(#glmTR)" />
      <path d="M12 22.4 L2 12 L12 12 Z" fill="url(#glmBL)" />
      <path d="M12 22.4 L22 12 L12 12 Z" fill="url(#glmBR)" />
      {/* cut-stone table */}
      <path d="M12 6.4 L17.4 12 L12 17.6 L6.6 12 Z" fill="url(#glmTab)" />
      <path
        d="M12 6.4 L17.4 12 L12 17.6 L6.6 12 Z"
        fill="none"
        stroke="#7d521a"
        strokeOpacity="0.45"
        strokeWidth="0.45"
      />
      {/* facet seams from table corners to points */}
      <path
        d="M12 1.6 L12 6.4 M22 12 L17.4 12 M12 22.4 L12 17.6 M2 12 L6.6 12"
        stroke="#fff3ce"
        strokeOpacity="0.5"
        strokeWidth="0.45"
      />
      {/* rim: lit top-left, shadowed bottom-right */}
      <path
        d="M2 12 L12 1.6 L22 12"
        fill="none"
        stroke="#fff3ce"
        strokeOpacity="0.85"
        strokeWidth="0.55"
      />
      <path
        d="M22 12 L12 22.4 L2 12"
        fill="none"
        stroke="#4a2f0c"
        strokeOpacity="0.9"
        strokeWidth="0.55"
      />
      {/* soft inner glow + sparkle */}
      <path d="M12 1.6 L22 12 L12 22.4 L2 12 Z" fill="url(#glmGlow)" />
      <path
        d="M9.7 8.2 l0.55 1.45 1.45 0.55 -1.45 0.55 -0.55 1.45 -0.55 -1.45 -1.45 -0.55 1.45 -0.55 Z"
        fill="#fffbe9"
      />
    </svg>
  );
}
